import dayjs from 'dayjs';
// These locale imports for dayjs also _register_ the relevant locales
import deDEDayjsData from 'dayjs/locale/de';
import enGbDayjsData from 'dayjs/locale/en-gb';
import pseudoDayjsData from 'dayjs/locale/x-pseudo';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import calendar from 'dayjs/plugin/calendar';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import updateLocale from 'dayjs/plugin/updateLocale';

import { Duration } from '../Duration';
import {
  getDateMediumWithoutYearFormat,
  getDateShortWithoutYearFormat,
  pseudoLocale as dayjsPseudoLocale,
} from '../localeUtils';

import { DATE_MEDIUM_WITHOUT_YEAR, DATE_WITHOUT_YEAR, FROM_NOW, PSEUDO_LOCALE_CODE, RELATIVE_DAY } from './constants';
import { decode } from './decodeFormat';
import { Format } from './formats';

dayjs.extend(relativeTime);
dayjs.extend(updateLocale);
dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(calendar);
dayjs.extend(duration);

// This maps i18next locales to dayjs and numbro locales.
export interface LocalisedStrings {
  justNow: () => string;
  future: (duration: string) => string;
  past: (duration: string) => string;
  days: (count: number) => string;
  weeks: (count: number) => string;
  months: (count: number) => string;
  years: (count: number) => string;
}

export interface CalendarStrings {
  sameDay: string;
  lastDay: string;
  sameElse: string;
}
export interface MapLocales {
  [locale: string]: {
    relativeTime: ILocale['relativeTime'];
    dayjsLocale: string;
    localisedStrings: LocalisedStrings;
    calendar: CalendarStrings;
  };
}

export const mapLocales: MapLocales = {
  'en-GB': {
    relativeTime: enGbDayjsData.relativeTime,
    dayjsLocale: 'en-gb',
    localisedStrings: {
      justNow: () => 'Just now',
      future: (duration) => `in ${duration}`,
      past: (duration) => `${duration} ago`,
      days: (count) => {
        return count === 1 ? `1 day` : `${count} days`;
      },
      weeks: (count) => {
        return count === 1 ? `1 week` : `${count} weeks`;
      },
      months: (count) => {
        return count === 1 ? `1 month` : `${count} months`;
      },
      years: (count) => {
        return count === 1 ? `1 year` : `${count} years`;
      },
    },
    calendar: {
      sameDay: '[Today, ]LT',
      lastDay: '[Yesterday, ]LT',
      sameElse: 'll[, ]LT',
    },
  },
  'en-US': {
    // US locale is just the default, so not exported properly from the dayjs
    // So just use en-GB - relative date/time formats are the same
    relativeTime: enGbDayjsData.relativeTime,
    dayjsLocale: 'en',
    localisedStrings: {
      justNow: () => 'Just now',
      future: (duration) => `in ${duration}`,
      past: (duration) => `${duration} ago`,
      days: (count) => {
        return count === 1 ? `1 day` : `${count} days`;
      },
      weeks: (count) => {
        return count === 1 ? `1 week` : `${count} weeks`;
      },
      months: (count) => {
        return count === 1 ? `1 month` : `${count} months`;
      },
      years: (count) => {
        return count === 1 ? `1 year` : `${count} years`;
      },
    },
    calendar: {
      sameDay: '[Today, ]LT',
      lastDay: '[Yesterday, ]LT',
      sameElse: 'll[, ]LT',
    },
  },
  'de-DE': {
    relativeTime: deDEDayjsData.relativeTime,
    dayjsLocale: 'de',
    localisedStrings: {
      justNow: () => 'Gerade jetzt',
      future: (duration) => `in ${duration}`,
      past: (duration) => `${duration} vor`,
      days: (count) => {
        return count === 1 ? `1 Tag` : `${count} Tage`;
      },
      weeks: (count) => {
        return count === 1 ? `1 Woche` : `${count} Wochen`;
      },
      months: (count) => {
        return count === 1 ? `1 Monat` : `${count} Monats`;
      },
      years: (count) => {
        return count === 1 ? `1 Jahr` : `${count} Jahre`;
      },
    },
    calendar: {
      sameDay: '[Heute, ]LT',
      lastDay: '[Gestern, ]LT',
      sameElse: 'll[, ]LT',
    },
  },
};

if (process.env.NODE_ENV !== 'production') {
  mapLocales[PSEUDO_LOCALE_CODE] = {
    relativeTime: pseudoDayjsData.relativeTime as ILocale['relativeTime'],
    dayjsLocale: 'x-pseudo',
    localisedStrings: {
      justNow: () => 'Jús~t ñó~w',
      future: (duration) => `í~ñ ${duration}`,
      past: (duration) => `${duration} á~gó`,
      days: (count) => {
        return count === 1 ? `1 ~dáý` : `${count} d~áýs`;
      },
      weeks: (count) => {
        return count === 1 ? `1 ~wéé~k` : `${count} wéé~ks`;
      },
      months: (count) => {
        return count === 1 ? `1 ~mon~th` : `${count} ~mon~ths`;
      },
      years: (count) => {
        return count === 1 ? `1 ~yea~r` : `${count} ~yea~rs`;
      },
    },
    calendar: {
      sameDay: '[Tôδá¥, ]LT',
      lastDay: '[Ýèƨƭèřδá¥, ]LT',
      sameElse: 'll[, ]LT',
    },
  };

  // Set up the pseudo locale in dayJS
  dayjs.updateLocale('x-pseudo', dayjsPseudoLocale);
}

export const formatDate = (value: Date, decodedFormat: string | undefined) => {
  switch (decodedFormat) {
    case DATE_WITHOUT_YEAR:
      return dayjs(value).format(getDateShortWithoutYearFormat());
    case DATE_MEDIUM_WITHOUT_YEAR:
      return dayjs(value).format(getDateMediumWithoutYearFormat());
    case FROM_NOW:
      return dayjs(value).fromNow();
    case RELATIVE_DAY:
      return dayjs(value).calendar();
    default:
      return dayjs(value).format(decodedFormat || decode(Format.DATE_MEDIUM));
  }
};
export const formatDuration = (value: Duration) => {
  return dayjs
    .duration({
      days: value.days,
      weeks: value.weeks,
      months: value.months,
      years: value.years,
    })
    .humanize();
};
